import { useCallback, useState, useEffect } from 'react';
import { noop } from 'lodash';
import { DEFAULT_ITEMS_COUNT_TO_PRELOAD } from '../../constants';
import type { LightboxContextProviderProps } from '../../LightboxContext';
import { preloadNextSources, preloadPrevSources, preloadSourcesDependingOnIndex } from './utils';

interface UseLightboxProps {
    initialSilentMode: LightboxContextProviderProps['initialSilentMode'];
    sources: LightboxContextProviderProps['sources'];
    sourceInitialIndex: LightboxContextProviderProps['sourceInitialIndex'];
    onClickNext: LightboxContextProviderProps['onClickNext'];
    onClickPrev: LightboxContextProviderProps['onClickPrev'];
    itemsToPreload?: LightboxContextProviderProps['itemsToPreload'];
}

const useLightbox = ({
    initialSilentMode = true,
    sourceInitialIndex = 0,
    sources,
    onClickNext = noop,
    onClickPrev = noop,
    itemsToPreload = DEFAULT_ITEMS_COUNT_TO_PRELOAD,
}: UseLightboxProps) => {
    const [selectedSourceIndex, setSelectedSourceIndex] = useState<number>(sourceInitialIndex);
    const [silentMode, setSilentMode] = useState(initialSilentMode);

    const maxSourceIndex = sources.length - 1;

    const handlePrev = useCallback(() => {
        if (selectedSourceIndex === 0) {
            return;
        }
        onClickPrev();
        preloadPrevSources(sources, selectedSourceIndex, itemsToPreload);
        setSelectedSourceIndex((prev) => prev - 1);
    }, [selectedSourceIndex, onClickPrev, sources, itemsToPreload]);

    const handleNext = useCallback(() => {
        if (selectedSourceIndex === maxSourceIndex) {
            return;
        }
        preloadNextSources(sources, selectedSourceIndex, itemsToPreload);
        onClickNext();
        setSelectedSourceIndex((prev) => prev + 1);
    }, [selectedSourceIndex, maxSourceIndex, sources, itemsToPreload, onClickNext]);

    // Set local selectedIndex when sourceInitialIndex updates
    useEffect(() => {
        setSelectedSourceIndex(sourceInitialIndex);
    }, [sourceInitialIndex]);

    useEffect(() => {
        setSilentMode(initialSilentMode);
    }, [initialSilentMode]);

    // Preload next/prev sources on initial lightbox render
    useEffect(() => {
        if (itemsToPreload > 0) {
            preloadSourcesDependingOnIndex(sources, sourceInitialIndex, itemsToPreload);
        }
    }, [sources, sourceInitialIndex, itemsToPreload]);

    return {
        silentMode,
        setSilentMode,
        selectedSourceIndex,
        maxSourceIndex,
        sources,
        nextSource: handleNext,
        prevSource: handlePrev,
    };
};

export default useLightbox;
